const DEVICE_ID_STORAGE_KEY = 'device_id';
const LEGACY_DEVICE_ID_STORAGE_KEY = 'deviceId';
const DEVICE_ID_COOKIE_NAME = 'device_id';
const DEVICE_ID_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
export const DEVICE_ID_HEADER = 'X-Device-ID';

const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getCookieStore = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  return document;
};

const readDeviceIdCookie = () => {
  const cookieStore = getCookieStore();

  if (!cookieStore?.cookie) {
    return null;
  }

  const cookieValue = cookieStore.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${DEVICE_ID_COOKIE_NAME}=`));

  if (!cookieValue) {
    return null;
  }

  const [, rawValue] = cookieValue.split('=');
  const decodedValue = decodeURIComponent(rawValue || '');
  return isValidDeviceId(decodedValue) ? decodedValue : null;
};

const writeDeviceIdCookie = (deviceId) => {
  const cookieStore = getCookieStore();

  if (!cookieStore || !isValidDeviceId(deviceId)) {
    return;
  }

  cookieStore.cookie = `${DEVICE_ID_COOKIE_NAME}=${encodeURIComponent(deviceId)}; Max-Age=${DEVICE_ID_COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
};

export const isValidDeviceId = (value) =>
  typeof value === 'string' && DEVICE_ID_PATTERN.test(value.trim());

export const storeDeviceId = (deviceId) => {
  const storage = getStorage();

  if (!isValidDeviceId(deviceId)) {
    return;
  }

  if (storage) {
    try {
      storage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
      storage.setItem(LEGACY_DEVICE_ID_STORAGE_KEY, deviceId);
    } catch {
      // Ignore storage failures so the request path can continue.
    }
  }

  writeDeviceIdCookie(deviceId);
};

const generateDeviceId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `device_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

export const getStoredDeviceId = () => {
  const storage = getStorage();

  if (storage) {
    try {
      const canonical = storage.getItem(DEVICE_ID_STORAGE_KEY);
      if (isValidDeviceId(canonical)) {
        return canonical;
      }

      const legacy = storage.getItem(LEGACY_DEVICE_ID_STORAGE_KEY);
      if (isValidDeviceId(legacy)) {
        storeDeviceId(legacy);
        return legacy;
      }
    } catch {
      // Fall through to cookie-backed recovery below.
    }
  }

  const cookieDeviceId = readDeviceIdCookie();
  if (cookieDeviceId) {
    storeDeviceId(cookieDeviceId);
    return cookieDeviceId;
  }

  return null;
};

export const getOrCreateDeviceId = () => {
  const existingDeviceId = getStoredDeviceId();
  if (existingDeviceId) {
    return existingDeviceId;
  }

  const nextDeviceId = generateDeviceId();
  storeDeviceId(nextDeviceId);
  return nextDeviceId;
};

export const getDeviceIdentityHeaders = () => {
  const deviceId = getOrCreateDeviceId();

  if (!deviceId) {
    return {};
  }

  return {
    [DEVICE_ID_HEADER]: deviceId,
  };
};
